/**
 * 操作管理服务
 */
import { apiClient } from "../../../config/netConfig";

// 操作接口定义
export interface Operation {
  id: string;
  name: string;
  displayName?: string;
  code: string;
  description?: string;
  is_active: boolean;
  is_system: boolean;
  created_at: string;
  updated_at: string;
  service?: string;
  service_name?: string;
  serviceName?: string;
  module?: string;
  action?: string;
  page?: string;
  type?: string;
  status?: "active" | "inactive";
}

// 分页参数接口
export interface PaginationParams {
  page: number;
  pageSize: number;
}

// 操作查询参数接口
export interface OperationQueryParams extends PaginationParams {
  service?: string;
  status?: string;
  keyword?: string;
}

// 操作列表响应接口
export interface OperationListResponse {
  success: boolean;
  data: {
    items: Operation[];
    total: number;
    page: number;
    pageSize: number;
  };
  message: string;
}

// 枚举选项接口
export interface EnumOption {
  key?: string;
  label: string;
  value: string;
}

/**
 * 获取所有操作列表（不分页，获取全部）
 */
export const getAllOperations = async (): Promise<Operation[]> => {
  try {
    // 传递大的pageSize参数以获取所有操作
    const response = await apiClient.get("/operation/all", {
      params: {
        current: 1,
        pageSize: 10000, // 设置一个足够大的数字获取所有操作
      },
    });

    if (response.data?.success && response.data?.data?.items) {
      return response.data.data.items.map((operation: any) => ({
        id: operation.id.toString(),
        name: operation.name,
        code: operation.code,
        description: operation.description || "",
        is_active: operation.isActive ?? true,
        is_system: operation.isSystem ?? false,
        created_at: operation.created_at || new Date().toISOString(),
        updated_at: operation.updated_at || new Date().toISOString(),
        service_name: operation.service_name || operation.service,
        serviceName: operation.serviceName || operation.service_name || operation.service,
        module: operation.module,
        action: operation.action,
        page: operation.page,
        type: operation.type,
        status: operation.isActive ? "active" : "inactive",
      }));
    }

    return [];
  } catch (error) {
    console.error("获取权限列表失败:", error);
    throw error;
  }
};

/**
 * 获取权限列表（分页）
 */
export const getOperationList = async (params: OperationQueryParams): Promise<OperationListResponse> => {
  try {
    const response = await apiClient.get("/operation", { params });
    return response.data;
  } catch (error) {
    console.error("获取操作列表失败:", error);
    throw error;
  }
};

/**
 * 创建操作
 */
export const createOperation = async (operationData: Partial<Operation>): Promise<any> => {
  try {
    const response = await apiClient.post("/operation", operationData);
    return response.data;
  } catch (error) {
    console.error("创建操作失败:", error);
    throw error;
  }
};

/**
 * 更新操作
 */
export const updateOperation = async (id: string, operationData: Partial<Operation>): Promise<any> => {
  try {
    const response = await apiClient.put(`/operation/${id}`, operationData);
    return response.data;
  } catch (error) {
    console.error("更新操作失败:", error);
    throw error;
  }
};

/**
 * 删除操作
 */
export const deleteOperation = async (id: string): Promise<void> => {
  try {
    const response = await apiClient.delete(`/operation/${id}`);
    if (!response.data.success) {
      // 提取后端返回的具体错误信息
      const errorMessage = response.data.message || "删除操作失败";
      throw new Error(errorMessage);
    }
  } catch (error: any) {
    console.error("删除操作失败:", error);
    // 如果是 AxiosError，尝试从响应数据中提取错误信息
    if (error?.response?.data) {
      const errorData = error.response.data;
      // 如果响应数据包含 success: false 和 message，使用该 message
      if (errorData.success === false && errorData.message) {
        throw new Error(errorData.message);
      }
      // 如果响应数据包含 error 字段
      if (errorData.error) {
        throw new Error(errorData.error);
      }
      // 如果响应数据本身就是字符串
      if (typeof errorData === "string") {
        throw new Error(errorData);
      }
    }
    // 如果已经是 Error 对象，直接抛出
    if (error instanceof Error) {
      throw error;
    }
    // 否则抛出通用错误
    throw new Error(error?.message || "删除操作失败");
  }
};

/**
 * 批量删除操作
 */
export const batchDeleteOperations = async (ids: string[]): Promise<void> => {
  try {
    const response = await apiClient.post("/operation/batch-delete", { ids });
    if (!response.data.success) {
      throw new Error(response.data.message || "批量删除操作失败");
    }
  } catch (error) {
    console.error("批量删除操作失败:", error);
    throw error;
  }
};

/**
 * 根据操作代码获取关联的角色
 */
export const getRolesByOperationCode = async (operationCode: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/operation/${operationCode}/roles`);
    if (response.data?.success && response.data?.data) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    return [];
  } catch (error) {
    console.error("获取操作关联角色失败:", error);
    // 如果接口不存在，返回空数组
    return [];
  }
};

/**
 * 根据操作代码获取关联的管理员
 */
export const getAdminsByOperationCode = async (operationCode: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/operation/${operationCode}/admins`);
    if (response.data?.success && response.data?.data) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    return [];
  } catch (error) {
    console.error("获取操作关联管理员失败:", error);
    // 如果接口不存在，返回空数组
    return [];
  }
};

/**
 * 根据操作ID获取关联的角色
 */
export const getRolesByOperationId = async (operationId: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/operation/${operationId}/roles`);
    if (response.data?.success && response.data?.data) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    return [];
  } catch (error) {
    console.error("获取操作关联角色失败:", error);
    return [];
  }
};

/**
 * 根据操作ID获取关联的管理员
 */
export const getAdminsByOperationId = async (operationId: string): Promise<any[]> => {
  try {
    const response = await apiClient.get(`/operation/${operationId}/admins`);
    if (response.data?.success && response.data?.data) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    return [];
  } catch (error) {
    console.error("获取操作关联管理员失败:", error);
    return [];
  }
};

/**
 * 获取操作枚举选项
 */
export const getOperationEnums = async (): Promise<{
  services: EnumOption[];
  actions: EnumOption[];
  pages: EnumOption[];
  statuses: EnumOption[];
}> => {
  try {
    // 从微服务管理API动态获取服务列表
    let services: EnumOption[] = [];
    try {
      const serviceResponse = await apiClient.get("/services", {
        params: { page: 1, limit: 100 },
      });

      if (serviceResponse.data?.success && serviceResponse.data?.data?.services) {
        services = serviceResponse.data.data.services
          .filter((s: any) => s.isActive !== false) // 只显示启用的服务
          .map((s: any) => ({
            label: s.displayName || s.serviceName,
            value: s.serviceName,
          }));
      }
    } catch (error) {
      console.warn("无法获取微服务列表，使用默认选项", error);
      // 如果获取失败，使用基本的默认选项
      services = [{ label: "msadmin", value: "msadmin" }];
    }

    return {
      services,
      actions: [
        { label: "查看", value: "view" },
        { label: "创建", value: "create" },
        { label: "更新", value: "update" },
        { label: "删除", value: "delete" },
        { label: "导出", value: "export" },
        { label: "导入", value: "import" },
      ],
      pages: [
        { label: "admins", value: "admins" },
        { label: "roles", value: "roles" },
        { label: "permissions", value: "permissions" },
        { label: "services", value: "services" },
        { label: "assignment", value: "assignment" },
      ],
      statuses: [
        { label: "启用", value: "active" },
        { label: "禁用", value: "inactive" },
      ],
    };
  } catch (error) {
    console.error("获取权限枚举失败:", error);
    throw error;
  }
};
